import { useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { Printer, ChevronLeft, CheckCircle, XCircle, Truck, Package, RotateCcw } from 'lucide-react';
import { useGatePass, useTransitionGatePass } from '../../hooks/useGatePasses.js';
import { useAuth } from '../../context/AuthContext.jsx';
import StatusBadge from '../../components/ui/Badge.jsx';
import Button from '../../components/ui/Button.jsx';
import Card from '../../components/ui/Card.jsx';
import Modal from '../../components/ui/Modal.jsx';
import ConfirmDialog from '../../components/ui/ConfirmDialog.jsx';
import FileUpload from '../../components/FileUpload/FileUpload.jsx';
import GatePassPrint from '../../components/Print/GatePassPrint.jsx';
import { GP_TYPE_LABELS, GP_SUBTYPE_LABELS } from '../../utils/constants.js';
import { formatIST } from '../../utils/formatters.js';
import {
  canApproveGatePass, canCancelGatePass,
  canAcknowledgeDispatch, canAcknowledgeReceipt,
} from '../../utils/permissions.js';

const TERMINAL = ['RECEIVED','DELIVERED','RETURNED','ACCEPTED','REJECTED','CANCELLED'];
const LOCKED_AFTER = ['APPROVED','DISPATCHED','RECEIVED','DELIVERED','RETURNED','ACCEPTED','REJECTED','CANCELLED'];

export default function GatePassDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const printRef = useRef(null);
  const { data: gp, isLoading, refetch } = useGatePass(id);
  const transition = useTransitionGatePass(id);

  const [actionModal, setActionModal] = useState(null); // { action, title, needsRemark }
  const [remarks, setRemarks] = useState('');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [driverName, setDriverName] = useState('');
  const [driverContact, setDriverContact] = useState('');
  const [cancelDialog, setCancelDialog] = useState(false);

  const handlePrint = useReactToPrint({ contentRef: printRef });

  const doTransition = async (action, extra = {}) => {
    await transition.mutateAsync({ action, remarks, vehicleNumber, driverName, driverContact, ...extra });
    setActionModal(null); setRemarks(''); setVehicleNumber(''); setDriverName(''); setDriverContact('');
  };

  if (isLoading) return <div className="p-8 text-center text-gray-400">Loading…</div>;
  if (!gp) return <div className="p-8 text-center text-red-500">Gate pass not found.</div>;

  const isLocked = LOCKED_AFTER.includes(gp.status);
  const isTerminal = TERMINAL.includes(gp.status);

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between no-print">
        <div className="flex items-center gap-3">
          <Link to="/gate-passes" className="text-gray-400 hover:text-gray-600"><ChevronLeft className="w-5 h-5" /></Link>
          <div>
            <h1 className="text-xl font-bold text-gray-900 font-mono">{gp.number}</h1>
            <p className="text-sm text-gray-500">{GP_TYPE_LABELS[gp.type]}{gp.subType ? ` · ${GP_SUBTYPE_LABELS[gp.subType] || gp.subType}` : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={gp.status} />
          {gp.status === 'APPROVED' && (
            <Button variant="secondary" size="sm" icon={Printer} onClick={handlePrint}>Print</Button>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="no-print flex flex-wrap gap-2">
        {gp.status === 'DRAFT' && ['SUPER_ADMIN','ADMIN','MANAGER','STAFF'].includes(user.role) && (
          <Button size="sm" onClick={() => doTransition('SUBMIT')}>Submit for Approval</Button>
        )}
        {gp.status === 'SUBMITTED' && canApproveGatePass(user) && (
          <>
            <Button size="sm" onClick={() => doTransition('CHECK')}>Check</Button>
            <Button size="sm" variant="danger" onClick={() => setActionModal({ action: 'REJECT', title: 'Reject Gate Pass', needsRemark: true })}>Reject</Button>
          </>
        )}
        {gp.status === 'CHECKED' && canApproveGatePass(user) && (
          <>
            <Button size="sm" variant="success" icon={CheckCircle} onClick={() => doTransition('APPROVE')}>Approve</Button>
            <Button size="sm" variant="danger" icon={XCircle} onClick={() => setActionModal({ action: 'REJECT', title: 'Reject Gate Pass', needsRemark: true })}>Reject</Button>
          </>
        )}
        {gp.status === 'APPROVED' && canAcknowledgeDispatch(user) && (
          <Button size="sm" icon={Truck} onClick={() => setActionModal({ action: 'DISPATCH', title: 'Acknowledge Dispatch', needsVehicle: true })}>Dispatch / Gate Out</Button>
        )}
        {gp.status === 'DISPATCHED' && canAcknowledgeReceipt(user) && (
          <Button size="sm" variant="success" icon={Package} onClick={() => doTransition('RECEIVE')}>Acknowledge Receipt</Button>
        )}
        {gp.status === 'ARRIVING' && canAcknowledgeReceipt(user) && (
          <Button size="sm" icon={Package} onClick={() => doTransition('SECURITY_IN_ACK')}>Security In Acknowledge</Button>
        )}
        {gp.status === 'SECURITY_IN_ACKNOWLEDGED' && canApproveGatePass(user) && (
          <Button size="sm" onClick={() => doTransition('QC_CHECK')}>Mark QC Checked</Button>
        )}
        {gp.status === 'QC_CHECKED' && canApproveGatePass(user) && (
          <>
            <Button size="sm" variant="success" onClick={() => doTransition('ACCEPT')}>Accept</Button>
            <Button size="sm" variant="danger" onClick={() => setActionModal({ action: 'REJECT', title: 'Reject', needsRemark: true })}>Reject</Button>
          </>
        )}
        {!isTerminal && gp.status !== 'DRAFT' && canCancelGatePass(user) && (
          <Button size="sm" variant="danger" onClick={() => setCancelDialog(true)}>Cancel GP</Button>
        )}
        {!isLocked && ['SUPER_ADMIN','ADMIN','MANAGER','STAFF'].includes(user.role) && (
          <Link to={`/gate-passes/${id}/edit`}><Button size="sm" variant="secondary">Edit</Button></Link>
        )}
      </div>

      {/* Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="Source & Destination">
          <div className="p-4 space-y-2 text-sm">
            <Row label="Source" value={`${gp.sourceLocation?.code} — ${gp.sourceLocation?.name}`} />
            <Row label="Destination" value={gp.destinationText || gp.destinationPPC?.name || '—'} />
            <Row label="Owner" value={gp.ownerType === 'MERCHANT_EXPORTER' ? gp.merchantExporter?.name : 'Sreeragam Exports Pvt. Ltd.'} />
            {gp.subType && <Row label="Sub-type" value={GP_SUBTYPE_LABELS[gp.subType] || gp.subType} />}
          </div>
        </Card>

        <Card title="Vehicle & Carrier">
          <div className="p-4 space-y-2 text-sm">
            <Row label="Vehicle No." value={gp.vehicleNumber || '—'} />
            <Row label="Driver" value={gp.driverName || '—'} />
            <Row label="Driver Contact" value={gp.driverContact || '—'} />
            {gp.actualDispatchAt && <Row label="Dispatched At" value={formatIST(gp.actualDispatchAt)} />}
            {gp.receivedAt && <Row label="Received At" value={formatIST(gp.receivedAt)} />}
          </div>
        </Card>

        {(gp.type === 'OUT_FP') && (
          <Card title="Export Details">
            <div className="p-4 space-y-2 text-sm">
              <Row label="Freight Forwarder" value={gp.freightForwarder || '—'} />
              <Row label="Shipping Line" value={gp.shippingLine || '—'} />
              <Row label="Vessel Name" value={gp.vesselName || '—'} />
              <Row label="Container No." value={gp.containerNumber || '—'} />
              <Row label="BL Reference" value={gp.blReference || '—'} />
            </div>
          </Card>
        )}

        <Card title="Approval Chain">
          <div className="p-4 space-y-2 text-sm">
            <Row label="Created By" value={`${gp.createdBy?.name} — ${formatIST(gp.createdAt)}`} />
            {gp.approvedBy && <Row label="Approved By" value={`${gp.approvedBy?.name} — ${formatIST(gp.approvedAt)}`} />}
            {gp.securityOut && <Row label="Gate Out" value={`${gp.securityOut?.name} — ${formatIST(gp.actualDispatchAt)}`} />}
            {gp.securityIn && <Row label="Gate In" value={`${gp.securityIn?.name} — ${formatIST(gp.receivedAt)}`} />}
            {gp.cancelReason && <Row label="Cancel Reason" value={gp.cancelReason} className="text-red-600" />}
            {gp.remarks && <Row label="Remarks" value={gp.remarks} />}
          </div>
        </Card>
      </div>

      {/* Items table */}
      {gp.items?.length > 0 && (
        <Card title="Items">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>{['Species / Product','Grade','Form','Qty (kg)','Cartons','Lot No.','QC'].map(h => (
                  <th key={h} className="text-left px-4 py-2 font-medium text-gray-600 text-xs">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {gp.items.map(item => (
                  <tr key={item.id}>
                    <td className="px-4 py-2">{item.species || item.productName || '—'}</td>
                    <td className="px-4 py-2 text-gray-500">{item.grade || '—'}</td>
                    <td className="px-4 py-2 text-gray-500">{item.form || '—'}</td>
                    <td className="px-4 py-2 font-medium">{item.quantity != null ? item.quantity.toLocaleString() : '—'}</td>
                    <td className="px-4 py-2 text-gray-500">{item.cartons || '—'}</td>
                    <td className="px-4 py-2 font-mono text-xs">{item.lotNumber || '—'}</td>
                    <td className="px-4 py-2">
                      {item.qcStatus !== 'PENDING' && (
                        <span className={"badge " + (item.qcStatus === 'PASSED' ? 'badge-approved' : 'badge-rejected')}>
                          {item.qcStatus}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Attachments */}
      <Card title={`Attachments (${gp.attachments?.length || 0})`}>
        <div className="p-4">
          <FileUpload
            gatePassId={id}
            existingFiles={gp.attachments || []}
            disabled={isTerminal}
            onUploaded={() => refetch()}
          />
        </div>
      </Card>

      {/* Linked GPs */}
      {(gp.linkedOutwardGP || gp.inwardGPs?.length > 0) && (
        <Card title="Linked Gate Passes">
          <div className="p-4 space-y-2">
            {gp.linkedOutwardGP && (
              <Link to={`/gate-passes/${gp.linkedOutwardGP.id}`} className="flex items-center gap-2 text-sm text-teal-700 hover:underline">
                ↑ Outward: <span className="font-mono">{gp.linkedOutwardGP.number}</span>
                <StatusBadge status={gp.linkedOutwardGP.status} />
              </Link>
            )}
            {gp.inwardGPs?.map(igp => (
              <Link key={igp.id} to={`/gate-passes/${igp.id}`} className="flex items-center gap-2 text-sm text-blue-700 hover:underline">
                ↓ Inward: <span className="font-mono">{igp.number}</span>
                <StatusBadge status={igp.status} />
              </Link>
            ))}
          </div>
        </Card>
      )}

      {/* Printable version */}
      <div className="hidden">
        <GatePassPrint ref={printRef} gp={gp} />
      </div>

      {/* Action modal */}
      <Modal open={!!actionModal} onClose={() => setActionModal(null)} title={actionModal?.title || ''} size="sm">
        <div className="space-y-4">
          {actionModal?.needsVehicle && (
            <>
              <input className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Vehicle number" value={vehicleNumber} onChange={e => setVehicleNumber(e.target.value)} />
              <input className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Driver name" value={driverName} onChange={e => setDriverName(e.target.value)} />
              <input className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Driver contact" value={driverContact} onChange={e => setDriverContact(e.target.value)} />
            </>
          )}
          {actionModal?.needsRemark && (
            <textarea className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" rows={3} placeholder="Reason / remarks" value={remarks} onChange={e => setRemarks(e.target.value)} />
          )}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="sm" onClick={() => setActionModal(null)}>Cancel</Button>
            <Button size="sm" loading={transition.isPending} onClick={() => doTransition(actionModal.action)}>Confirm</Button>
          </div>
        </div>
      </Modal>

      {/* Cancel confirm */}
      <ConfirmDialog
        open={cancelDialog} onClose={() => setCancelDialog(false)}
        onConfirm={() => { doTransition('CANCEL', { remarks }); setCancelDialog(false); }}
        loading={transition.isPending}
        title="Cancel Gate Pass"
        message="This will permanently cancel the gate pass. A reason is required."
        confirmLabel="Cancel Gate Pass"
        variant="danger"
      />
    </div>
  );
}

function Row({ label, value, className = '' }) {
  return (
    <div className="flex gap-2">
      <span className="text-gray-500 min-w-32 flex-shrink-0">{label}:</span>
      <span className={`font-medium ${className}`}>{value}</span>
    </div>
  );
}
